import { db } from '../firebase/config';
import { collection, addDoc, query, where, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import axios from 'axios';
import { nanoid } from 'nanoid';

// Utility functions for PKCE OAuth flow
const generateRandomString = (length: number) => {
  const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  let text = '';
  for (let i = 0; i < length; i++) {
    text += possible.charAt(Math.floor(Math.random() * possible.length));
  }
  return text;
};

const base64URLEncode = (str: ArrayBuffer): string => {
  return btoa(String.fromCharCode.apply(null, new Uint8Array(str) as unknown as number[]))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

const sha256 = async (plain: string): Promise<ArrayBuffer> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return window.crypto.subtle.digest('SHA-256', data);
};

// Canva application configuration
const CANVA_CONFIG = {
  appId: 'AAGko1e2TYQ',
  apiEndpoint: 'https://www.canva.com/api',
  oauthEndpoint: 'https://www.canva.com/api/oauth',
  redirectUri: `${window.location.protocol}//${window.location.host}/canva-callback`,
  scopes: ['designs.read', 'users.read', 'designs.write']
};

export type CanvaAssetCategory = 'organization' | 'event' | 'trip' | 'membership';

export interface CanvaDesign {
  id: string;
  title: string;
  thumbnail: string;
  url: string;
  width: number;
  height: number;
  created: string;
  modified: string;
}

export interface SavedCanvaAsset {
  id: string;
  designId: string;
  title: string;
  description?: string;
  category: CanvaAssetCategory;
  imageUrl: string;
  thumbnailUrl: string;
  canvaUrl: string;
  dimensions: {
    width: number;
    height: number;
  };
  metadata?: any;
  orgId: string;
  eventId?: string;
  tripId?: string;
  membershipId?: string;
  createdAt: string;
  updatedAt?: string;
}

const canvaIntegrationService = {
  /**
   * Get Canva OAuth URL for user authorization with PKCE
   */
  getAuthUrl: async (): Promise<{url: string, codeVerifier: string, state: string}> => {
    // Generate code verifier (random string between 43-128 chars)
    const codeVerifier = generateRandomString(96);
    
    // Generate state parameter to protect against CSRF
    const state = generateRandomString(32);
    
    // Generate code challenge from code verifier (SHA-256 hash + base64url encode)
    const codeChallenge = base64URLEncode(await sha256(codeVerifier));
    
    // Store the code verifier and state in localStorage for the callback
    localStorage.setItem('canva_code_verifier', codeVerifier);
    localStorage.setItem('canva_state', state);
    localStorage.setItem('canva_redirect_path', window.location.pathname);
    
    // Build the authorization URL based on Canva's documentation
    const authUrl = new URL(`${CANVA_CONFIG.oauthEndpoint}/authorize`);
    authUrl.searchParams.append('client_id', CANVA_CONFIG.appId);
    authUrl.searchParams.append('redirect_uri', CANVA_CONFIG.redirectUri);
    authUrl.searchParams.append('response_type', 'code');
    authUrl.searchParams.append('scope', CANVA_CONFIG.scopes.join(' '));
    authUrl.searchParams.append('state', state);
    authUrl.searchParams.append('code_challenge', codeChallenge);
    authUrl.searchParams.append('code_challenge_method', 'S256');
    

    
    return {
      url: authUrl.toString(),
      codeVerifier,
      state
    };
  },

  /**
   * Exchange authorization code for access token
   * Note: In production, this should happen server-side for security
   */
  exchangeCodeForToken: async (code: string): Promise<string> => {
    try {
      // Get code verifier from localStorage
      const codeVerifier = localStorage.getItem('canva_code_verifier');
      
      if (!codeVerifier) {
        throw new Error('Code verifier not found. Please try authenticating again.');
      }
      
      // Exchange code for token using the code verifier
      const response = await axios.post(
         `${process.env.REACT_APP_API_URL}/api/canva/token`,
         {
            code,
            code_verifier: codeVerifier,
         },
         {
            headers: {
               'Content-Type': 'application/json',
            },
         }
      );
      
      return response.data.access_token;
    } catch (error) {
      console.error('Error exchanging code for token:', error);
      throw new Error('Failed to authenticate with Canva');
    }
  },

  /**
   * Fetch user's designs from Canva
   */
  fetchUserDesigns: async (accessToken: string): Promise<CanvaDesign[]> => {
    try {
      // This should be a call to your backend, which will fetch designs using the token
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/canva/designs`, {
         headers: { Authorization: `Bearer ${accessToken}` },
      });
      return response.data.designs;
    } catch (error) {
      console.error('Error fetching Canva designs:', error);
      throw new Error('Failed to fetch designs from Canva');
    }
  },

  /**
   * Import a Canva design as an asset
   */
  importDesignAsAsset: async (
    design: CanvaDesign,
    orgId: string,
    category: CanvaAssetCategory,
    relatedId?: string,
    description?: string
  ): Promise<SavedCanvaAsset> => {
    try {
      // Prepare asset data
      const assetData: SavedCanvaAsset = {
        id: nanoid(),
        designId: design.id,
        title: design.title,
        description: description || '',
        category,
        imageUrl: design.url,
        thumbnailUrl: design.thumbnail,
        canvaUrl: `https://www.canva.com/design/${design.id}`,
        dimensions: {
          width: design.width,
          height: design.height
        },
        metadata: {
          source: 'canva',
          imported: new Date().toISOString()
        },
        orgId,
        createdAt: new Date().toISOString()
      };

      // Add related ID based on category
      if (relatedId) {
        switch (category) {
          case 'event':
            assetData.eventId = relatedId;
            break;
          case 'trip':
            assetData.tripId = relatedId;
            break;
          case 'membership':
            assetData.membershipId = relatedId;
            break;
        }
      }

      // Save to Firestore
      await addDoc(collection(db, 'organizations', orgId, 'canvaAssets'), assetData);

      return assetData;
    } catch (error) {
      console.error('Error importing Canva design:', error);
      throw new Error('Failed to import design from Canva');
    }
  },

  /**
   * Fetch Canva assets by organization and category
   */
  fetchCanvaAssets: async (
    orgId: string,
    category?: CanvaAssetCategory,
    relatedId?: string
  ): Promise<SavedCanvaAsset[]> => {
    try {
      let q = query(collection(db, 'organizations', orgId, 'canvaAssets'));

      // Apply filters if provided
      if (category) {
        q = query(q, where('category', '==', category));
      }

      // Filter by related ID based on category
      if (relatedId && category) {
        switch (category) {
          case 'event':
            q = query(q, where('eventId', '==', relatedId));
            break;
          case 'trip':
            q = query(q, where('tripId', '==', relatedId));
            break;
          case 'membership':
            q = query(q, where('membershipId', '==', relatedId));
            break;
        }
      }

      const querySnapshot = await getDocs(q);
      const assets: SavedCanvaAsset[] = [];

      querySnapshot.forEach((doc) => {
        assets.push(doc.data() as SavedCanvaAsset);
      });

      // Sort by creation date, newest first
      return assets.sort((a, b) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
    } catch (error) {
      console.error('Error fetching Canva assets:', error);
      throw new Error('Failed to load Canva assets');
    }
  },

  /**
   * Update a Canva asset
   */
  updateCanvaAsset: async (orgId: string, asset: SavedCanvaAsset): Promise<boolean> => {
    try {
      // Find the document reference
      const assetsRef = collection(db, 'organizations', orgId, 'canvaAssets');
      const q = query(assetsRef, where('id', '==', asset.id));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return false;
      }

      // Update the document
      const docRef = querySnapshot.docs[0].ref;
      await updateDoc(docRef, {
        ...asset,
        updatedAt: new Date().toISOString()
      });

      return true;
    } catch (error) {
      console.error('Error updating Canva asset:', error);
      throw new Error('Failed to update Canva asset');
    }
  },

  /**
   * Delete a Canva asset
   */
  deleteCanvaAsset: async (orgId: string, assetId: string): Promise<boolean> => {
    try {
      // Find the document reference
      const assetsRef = collection(db, 'organizations', orgId, 'canvaAssets');
      const q = query(assetsRef, where('id', '==', assetId));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return false;
      }

      // Delete the document
      await deleteDoc(querySnapshot.docs[0].ref);
      return true;
    } catch (error) {
      console.error('Error deleting Canva asset:', error);
      throw new Error('Failed to delete Canva asset');
    }
  }
};

export default canvaIntegrationService;
